import { translate } from "@/i18n";
import { onboardingContextualMessages } from "@/i18n/messages/onboarding-contextual";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useConversationStore } from "@/stores/conversation-store";
import { useCompanionStore } from "@/stores/companion-store";
import { contextualConversationExecution } from "./contextual-conversation-execution";
import { useCanvasStore } from "@/stores/canvas-store";
import { useTtsPlaybackStore } from "@/desktop/tts-playback-store";
import { dismissDiscoveryWorkerToasts } from "./discovery-worker-toasts";

/** Capture before sending, because the agent can reply before the HTTP receipt. */
export function captureContextualDecisionSubmission(awaitingInputId: string) {
  const story = useOnboardingStore.getState().contextualStory;
  if (!story?.basicsStage) return;
  const executionId = contextualConversationExecution(
    story,
    useConversationStore.getState().messages,
    useCompanionStore.getState().executions.values(),
  );
  const pending = [...useCanvasStore.getState().awaitingInputs.values()]
    .flat()
    .find((item) => item.id === awaitingInputId);
  const cursor = [...useCanvasStore.getState().agentCursors.values()].find(
    (item) => item.question?.awaitingInputId === awaitingInputId,
  );
  if (
    !executionId ||
    (pending?.executionId ?? cursor?.executionId) !== executionId
  )
    return;
  useOnboardingStore.setState({
    contextualStory: {
      ...story,
      decisionReplyBaseline: useConversationStore
        .getState()
        .messages.filter(
          (message) =>
            message.role === "assistant" &&
            message.executionId === executionId &&
            !message.activityKind,
        )
        .map((message) => message.id),
    },
  });
  useTtsPlaybackStore.setState((state) => ({
    completedReplyIds: state.completedReplyIds.filter(
      (id) => id !== executionId,
    ),
  }));
}

/** Retain the production receipt before its question disappears on any display. */
export function recordContextualTaskAcceptance(data: {
  status?: unknown;
  project_id?: unknown;
  execution_id?: unknown;
  conversation_id?: unknown;
  accepted_task_id?: unknown;
  saved_task_ids?: unknown;
  selected_option?: unknown;
  answered_at?: unknown;
  answer_delivery_status?: unknown;
}) {
  const story = useOnboardingStore.getState().contextualStory;
  if (
    !story ||
    !["looking", "live"].includes(story.phase) ||
    data.status !== "answered" ||
    data.project_id !== story.projectId ||
    typeof data.execution_id !== "string"
  )
    return;
  const acceptedId =
    typeof data.accepted_task_id === "string" && data.accepted_task_id
      ? data.accepted_task_id
      : null;
  const savedIds = Array.isArray(data.saved_task_ids)
    ? data.saved_task_ids.filter(
        (id): id is string => typeof id === "string" && !!id,
      )
    : [];
  const finish =
    !!story.basicsStage && data.selected_option === "finish_onboarding";
  if (!acceptedId && !savedIds.length && !finish) return;
  const executionId = contextualConversationExecution(
    story,
    useConversationStore.getState().messages,
    useCompanionStore.getState().executions.values(),
  );
  if (
    data.execution_id !== executionId &&
    !story.executionIds?.includes(data.execution_id) &&
    !(story.conversationId && story.conversationId === data.conversation_id)
  )
    return;
  const decision = acceptedId ? "start" : savedIds.length ? "save" : "finish";
  const answeredAt =
    typeof data.answered_at === "string" ? data.answered_at : undefined;
  if (
    story.confirmed &&
    ((acceptedId && story.acceptedTaskIds?.includes(acceptedId)) ||
      (savedIds.length &&
        savedIds.every((id) => story.savedTaskIds?.includes(id))) ||
      (finish && story.decision === "finish"))
  ) {
    // Recovered state can retain task IDs before their decision metadata.
    // Fill only missing metadata; replaying acceptance would clear later
    // launch failures and restart the settled execution boundary.
    if (
      story.basicsStage &&
      (!story.decision || story.decision === decision) &&
      (!story.decision || (!story.decisionAnsweredAt && answeredAt))
    )
      useOnboardingStore.setState({
        contextualStory: {
          ...story,
          decision,
          ...(!story.decisionAnsweredAt && answeredAt
            ? { decisionAnsweredAt: answeredAt }
            : {}),
        },
      });
    return "existing" as const;
  }
  const acceptedTaskIds = [
    ...new Set([
      ...(story.acceptedTaskIds ?? []),
      ...(acceptedId ? [acceptedId] : []),
    ]),
  ];
  useOnboardingStore.setState({
    contextualStory: {
      ...story,
      phase: "live",
      confirmed: true,
      retryState: null,
      settledExecutionId: null,
      ...(story.basicsStage
        ? {
            decision,
            ...(answeredAt ? { decisionAnsweredAt: answeredAt } : {}),
          }
        : {}),
      acceptedTaskIds,
      savedTaskIds: [...new Set([...(story.savedTaskIds ?? []), ...savedIds])],
      error:
        data.answer_delivery_status === "unavailable"
          ? translate(onboardingContextualMessages, "answerNotDelivered")
          : null,
    },
  });
  dismissDiscoveryWorkerToasts(story.projectId, acceptedTaskIds);
  return "recorded" as const;
}
